import { action, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';

import { formatMessage } from '../../translations/format-message';
import { ObservableInitialState } from '../initialState';
import * as baseService from '../service/base-service';
import {
  ICorrectionFileList,
  ICorrectionRequest,
  IPreviousRaisedRequests,
  IPreviousRequest,
} from '../../models/forms/ICorrectionControl';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
const appConfig = config();

export class CorrectionControlStore {
  initialStateValue = ObservableInitialState;
  saveTeplarOrderState = { ...this.initialStateValue };
  fileUploadState = { ...this.initialStateValue };
  uploadedFile: ICorrectionFileList | undefined = undefined;
  addCorrectionRequestState = { ...this.initialStateValue };
  getPreviousCorrectionRequestState = { ...this.initialStateValue };
  getCorrectionRequestByIdState = { ...this.initialStateValue };
  previousRequests: IPreviousRaisedRequests[] = [];
  deleteFileState = { ...this.initialStateValue };
  getPendingCountState = { ...this.initialStateValue };
  pendingCount: number = 0;

  constructor() {
    makeObservable(this, {
      fileUploadState: observable,
      addCorrectionRequestState: observable,
      uploadedFile: observable,
      getPreviousCorrectionRequestState: observable,
      previousRequests: observable,
      getCorrectionRequestByIdState: observable,
      getPendingCountState: observable,
      pendingCount: observable,
      FileUploadService: action,
      resetFileUploadState: action,
      AddCorrectionRequestService: action,
      resetAddCorrectionRequestState: action,
      GetPreviousCorrectionRequestService: action,
      resetGetPreviousCorrectionRequestState: action,
      deleteFileState: observable,
      DeleteFileService: action,
      resetDeleteFileState: action,
      GetCorrectionRequestByIdService: action,
      resetGetCorrectionRequestByIdState: action,
      resetStore: action,
      resetGetPendingCountState: action,
      GetPendingCountService: action,
    });
  }

  //#region Actions

  FileUploadService = (TenantId: number, data: any) => {
    this.fileUploadState.inProgress = true;
    const formData = new FormData();
    formData.append('File', data.File);
    let url = URLConstants.AddProductFile + '?TenantId=' + TenantId;
    return baseService
      .postMultipart(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(
        (response: IApiResponse<IApiSuccessResponse<ICorrectionFileList>>) => {
          if (response.data.Error) {
            this.fileUploadState.error = response.data.Message;
          } else {
            this.uploadedFile = response.data.Data;
            this.fileUploadState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.fileUploadState.error = err;
      })
      .finally(
        action(() => {
          this.fileUploadState.inProgress = false;
        })
      );
  };

  AddCorrectionRequestService = (data: ICorrectionRequest) => {
    this.addCorrectionRequestState.inProgress = true;
    const url = URLConstants.SaveCorrection;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addCorrectionRequestState.error = response.data.Message;
        } else {
          this.addCorrectionRequestState.success = true;
        }
      })
      .catch((err: string) => {
        this.addCorrectionRequestState.error = err;
      })
      .finally(
        action(() => {
          this.addCorrectionRequestState.inProgress = false;
        })
      );
  };

  GetPreviousCorrectionRequestService = (
    tenantId: number,
    trayId: number,
    processId: number
  ) => {
    this.getPreviousCorrectionRequestState.inProgress = true;
    const url = `${URLConstants.GetPreviousCorrectionRequest}?TenantId=${tenantId}&ProductId=${trayId}&ProcessId=${processId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IPreviousRequest>>) => {
        if (response.data.Error) {
          this.getPreviousCorrectionRequestState.error = response.data.Message;
        } else {
          this.previousRequests = response.data.Data?.PreviousRaisedRequests;
          this.getPreviousCorrectionRequestState.success = true;
        }
      })
      .catch((err: string) => {
        this.getPreviousCorrectionRequestState.error = err;
      })
      .finally(
        action(() => {
          this.getPreviousCorrectionRequestState.inProgress = false;
        })
      );
  };

  DeleteFileService = (filePath: string) => {
    this.deleteFileState.inProgress = true;
    const url = `${URLConstants.DeleteFile}`;
    const data = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      FileName: filePath,
    };
    return baseService
      .deleteArrayRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteFileState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteFileState.success = true;
      })
      .catch((err: string) => {
        this.deleteFileState.error = err;
      })
      .finally(
        action(() => {
          this.deleteFileState.inProgress = false;
        })
      );
  };

  GetCorrectionRequestByIdService = (tenantId: number, Id: number) => {
    this.getCorrectionRequestByIdState.inProgress = true;
    const url = `${URLConstants.GetCorrectionRequestById}/${tenantId}?CorrectionRequestId=${Id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IPreviousRequest>>) => {
        if (response.data.Error) {
          this.getCorrectionRequestByIdState.error = response.data.Message;
        } else {
          this.previousRequests = response.data.Data?.PreviousRaisedRequests;
          this.getCorrectionRequestByIdState.success = true;
        }
      })
      .catch((err: string) => {
        this.getCorrectionRequestByIdState.error = err;
      })
      .finally(
        action(() => {
          this.getCorrectionRequestByIdState.inProgress = false;
        })
      );
  };

  GetPendingCountService = (tenantId: number) => {
    this.getPendingCountState.inProgress = true;
    const url = `${URLConstants.GetPendingCount}/${tenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<number>>) => {
        if (response.data.Error) {
          this.getPendingCountState.error = response.data.Message;
        } else {
          this.pendingCount = response.data.Data;
          this.getPendingCountState.success = true;
        }
      })
      .catch((err: string) => {
        this.getPendingCountState.error = err;
      })
      .finally(
        action(() => {
          this.getPendingCountState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  //#endregion

  //#region reset
  resetFileUploadState = () => {
    this.fileUploadState = { ...this.initialStateValue };
  };

  resetAddCorrectionRequestState = () => {
    this.addCorrectionRequestState = { ...this.initialStateValue };
  };

  resetGetPreviousCorrectionRequestState = () => {
    this.getPreviousCorrectionRequestState = { ...this.initialStateValue };
  };

  resetDeleteFileState = () => {
    this.deleteFileState = { ...this.initialStateValue };
  };

  resetGetCorrectionRequestByIdState = () => {
    this.getCorrectionRequestByIdState = { ...this.initialStateValue };
  };

  resetGetPendingCountState = () => {
    this.getPendingCountState = { ...this.initialStateValue };
    this.pendingCount = 0;
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.fileUploadState = { ...this.initialStateValue };
    this.uploadedFile = undefined;
    this.addCorrectionRequestState = { ...this.initialStateValue };
    this.getPreviousCorrectionRequestState = { ...this.initialStateValue };
    this.previousRequests = [];
    this.deleteFileState = { ...this.initialStateValue };
    this.getCorrectionRequestByIdState = { ...this.initialStateValue };
    this.getPendingCountState = { ...this.initialStateValue };
    this.pendingCount = 0;
  };
  //#endregion
}

export default new CorrectionControlStore();
